/**
 * Back-Edge Detection
 *
 * Detects back-edges (edges that would create cycles) via DFS and identifies
 * which nodes are "feedback nodes" (off the critical path).
 *
 * Returns:
 *   backEdges      - Set of "parentId->childId" strings that are back-edges
 *   backEdgeList   - Array of { from, to } objects
 *   forwardChildrenMap - children map with back-edges removed
 *   feedbackNodes  - Set of nodeIds that are off the critical path
 */
export function detectBackEdgesAndFeedbackNodes_phase1(tasks, childrenMap, parentsMap) {
  const backEdges = new Set();
  const backEdgeList = [];
  const visited = new Set();
  const inStack = new Set();

  const roots = tasks.filter(task => (parentsMap.get(task.id) || []).length === 0);
  const sortedRoots = [...roots].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  const sortedTasks = [...tasks].sort((a, b) => (a.id || '').localeCompare(b.id || ''));

  function dfs(nodeId) {
    visited.add(nodeId);
    inStack.add(nodeId);

    const children = childrenMap.get(nodeId) || [];
    const sortedChildren = [...children].sort((a, b) => (a || '').localeCompare(b || ''));

    for (const childId of sortedChildren) {
      if (inStack.has(childId)) {
        backEdges.add(`${nodeId}->${childId}`);
        backEdgeList.push({ from: nodeId, to: childId });
      } else if (!visited.has(childId)) {
        dfs(childId);
      }
    }

    inStack.delete(nodeId);
  }

  for (const root of sortedRoots) {
    if (!visited.has(root.id)) dfs(root.id);
  }
  for (const task of sortedTasks) {
    if (!visited.has(task.id)) dfs(task.id);
  }

  // Build forward-only children map (back-edges removed)
  const forwardChildrenMap = new Map();
  tasks.forEach(task => forwardChildrenMap.set(task.id, []));
  for (const [parentId, children] of childrenMap) {
    for (const childId of children) {
      if (!backEdges.has(`${parentId}->${childId}`)) {
        forwardChildrenMap.get(parentId).push(childId);
      }
    }
  }

  // Real terminals: no forward children AND not a back-edge source
  const backEdgeSourceIds = new Set(backEdgeList.map(e => e.from));
  const terminalNodes = new Set();
  for (const task of tasks) {
    const forwardChildren = forwardChildrenMap.get(task.id) || [];
    if (forwardChildren.length === 0 && !backEdgeSourceIds.has(task.id)) {
      terminalNodes.add(task.id);
    }
  }

  // Can a node reach a real terminal via forward edges?
  const canReachTerminal = new Map();
  function checkCanReachTerminal(nodeId, visitedSet = new Set()) {
    if (canReachTerminal.has(nodeId)) return canReachTerminal.get(nodeId);
    if (visitedSet.has(nodeId)) return false;
    if (terminalNodes.has(nodeId)) { canReachTerminal.set(nodeId, true); return true; }
    visitedSet.add(nodeId);
    for (const childId of (forwardChildrenMap.get(nodeId) || [])) {
      if (checkCanReachTerminal(childId, new Set(visitedSet))) {
        canReachTerminal.set(nodeId, true);
        return true;
      }
    }
    canReachTerminal.set(nodeId, false);
    return false;
  }
  for (const task of tasks) checkCanReachTerminal(task.id);

  // Can a node reach a back-edge source via forward edges?
  const canReachBackEdgeSource = new Map();
  function checkCanReachBackEdgeSource(nodeId, visitedSet = new Set()) {
    if (canReachBackEdgeSource.has(nodeId)) return canReachBackEdgeSource.get(nodeId);
    if (visitedSet.has(nodeId)) return false;
    if (backEdgeSourceIds.has(nodeId)) { canReachBackEdgeSource.set(nodeId, true); return true; }
    visitedSet.add(nodeId);
    for (const childId of (forwardChildrenMap.get(nodeId) || [])) {
      if (checkCanReachBackEdgeSource(childId, new Set(visitedSet))) {
        canReachBackEdgeSource.set(nodeId, true);
        return true;
      }
    }
    canReachBackEdgeSource.set(nodeId, false);
    return false;
  }
  for (const task of tasks) checkCanReachBackEdgeSource(task.id);

  // Identify feedback nodes
  const feedbackNodes = new Set(backEdgeSourceIds);
  for (const task of tasks) {
    if (roots.some(r => r.id === task.id)) continue;
    if (feedbackNodes.has(task.id)) continue;
    if (!canReachTerminal.get(task.id) && !canReachBackEdgeSource.get(task.id)) {
      feedbackNodes.add(task.id);
    }
  }

  return { backEdges, backEdgeList, forwardChildrenMap, feedbackNodes, roots };
}

/**
 * Detect nested back-edges within feedback loops.
 * Mutates backEdges and backEdgeList in place.
 */
export function detectNestedBackEdges(backEdgeList, backEdges, feedbackNodes, childrenMap, parentsMap) {
  for (const primaryBackEdge of backEdgeList) {
    const loopFeedbackNodes = new Set();
    const queue = [primaryBackEdge.from];
    const visited = new Set();

    while (queue.length > 0) {
      const nodeId = queue.shift();
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);

      if (feedbackNodes.has(nodeId)) {
        loopFeedbackNodes.add(nodeId);
        for (const parentId of (parentsMap.get(nodeId) || [])) {
          const edgeKey = `${parentId}->${nodeId}`;
          if (!backEdges.has(edgeKey) && feedbackNodes.has(parentId)) {
            queue.push(parentId);
          }
        }
      }
    }

    for (const nodeId of loopFeedbackNodes) {
      for (const childId of (childrenMap.get(nodeId) || [])) {
        if (!loopFeedbackNodes.has(childId)) continue;
        const childParents = parentsMap.get(childId) || [];
        const feedbackParents = childParents.filter(pid => loopFeedbackNodes.has(pid));
        if (feedbackParents.length <= 1) continue;

        const edgeKey = `${nodeId}->${childId}`;
        if (backEdges.has(edgeKey)) continue;

        const nodeParents = parentsMap.get(nodeId) || [];
        const hasFeedbackParent = nodeParents.some(pid => loopFeedbackNodes.has(pid));
        if (hasFeedbackParent) {
          console.log('[BackEdgeDetection] Found nested back-edge:', nodeId.slice(-8), '->', childId.slice(-8));
          backEdges.add(edgeKey);
          backEdgeList.push({ from: nodeId, to: childId, isNested: true });
        }
      }
    }
  }
}
